import { Hub } from '@aws-amplify/core';
import { t } from 'i18next';

import QuestionsService from './QuestionsService';

import { Answers } from 'types/zod/answers';
import { type Question } from 'types/zod/question';
import { Section } from 'types/zod/section';
import { type SectionStatus } from 'types/zod/section-status';

export const SECTIONS_CHANNEL = 'sectionsChannel';
export const SET_CURRENT_SECTION = 'setCurrentSection';
export const SET_CURRENT_SUB_SECTION = 'setCurrentSubSection';
export const SET_CURRENT_PRODUCT_CONTEXT = 'setCurrentProductContext';
export const NAVIGATE_TO_SECTION = 'navigateToSection';
export const RESUME = 'resume';

export interface QuestionnaireProgress {
    isStarted: boolean;
    isFinished: boolean;
}

const SectionsService = {
    questionsService: new QuestionsService(),

    setCurrentSection: (section: string) => {
        Hub.dispatch(SECTIONS_CHANNEL, {
            event: SET_CURRENT_SECTION,
            message: '',
            data: {
                section: section,
            },
        });
    },

    setCurrentSubSection: (subSection: string) => {
        Hub.dispatch(SECTIONS_CHANNEL, {
            event: SET_CURRENT_SUB_SECTION,
            message: '',
            data: {
                subSection: subSection,
            },
        });
    },

    setCurrentProductContext: (context: string | undefined) => {
        Hub.dispatch(SECTIONS_CHANNEL, {
            event: SET_CURRENT_PRODUCT_CONTEXT,
            message: '',
            data: {
                context: context,
            },
        });
    },

    navigateToSection: (section: string, sections: string[], currentSection: string) => {
        const isForwardDirection = sections.indexOf(section) > sections.indexOf(currentSection);
        SectionsService.navigateToSectionInternal(section, isForwardDirection);
    },

    navigateToCurrentSectionStart: (section: string) => {
        SectionsService.navigateToSectionInternal(section, false);
    },

    navigateToSectionInternal: (section: string, isForwardDirection: boolean) => {
        Hub.dispatch(SECTIONS_CHANNEL, {
            event: NAVIGATE_TO_SECTION,
            message: '',
            data: {
                section: section,
                isForwardDirection: isForwardDirection,
            },
        });
    },

    resume: (isFromLoop = false) => {
        Hub.dispatch(SECTIONS_CHANNEL, {
            event: RESUME,
            message: '',
            data: {
                isFromLoop: isFromLoop,
            },
        });
    },

    resumeFromLoop: () => {
        SectionsService.resume(true);
    },

    getSections: (questionnaire: Question): Section[] => {
        const sections = [] as Section[];
        let lastSection = '';
        const stack = [] as Question[];
        stack.push(questionnaire);

        while (stack.length > 0) {
            const currentQuestion = stack.pop() as Question;

            const currentSection = currentQuestion._section as string;
            const shouldAddSection = !!currentSection && lastSection !== currentSection;

            if (shouldAddSection) {
                lastSection = currentSection;
                const question = {
                    _fullPath: currentQuestion._fullPath,
                    _on: currentQuestion._section_on ?? currentQuestion._on,
                } as Question;
                sections.push({
                    name: currentSection,
                    questionForCondition: question,
                    status: 'notStarted',
                    questionnaire: {},
                });
            }

            Object.keys(currentQuestion)
                .filter((key) => !SectionsService.questionsService.isSpecialField(key))
                .reverse()
                .forEach((key) => {
                    stack.push(currentQuestion[key] as Question);
                });
        }

        SectionsService.splitQuestionnaireBySections(questionnaire, sections);

        return sections;
    },

    splitQuestionnaireBySections: (questionnaire: Question, sections: Section[]) => {
        const firstLevelKeys = Object.keys(questionnaire).filter(
            (key) => !SectionsService.questionsService.isSpecialField(key),
        );
        let currentSection = null;
        for (const firstLevelKey of firstLevelKeys) {
            if (questionnaire[firstLevelKey]._section) {
                currentSection = sections.find((s) => s.name === questionnaire[firstLevelKey]._section);
            }

            const secondLevelKeys = Object.keys(questionnaire[firstLevelKey]).filter(
                (key) => !SectionsService.questionsService.isSpecialField(key),
            );
            const firstLevelQuestionOptions = Object.keys(questionnaire[firstLevelKey]).filter((key) =>
                SectionsService.questionsService.isSpecialField(key),
            );

            if (currentSection) {
                currentSection.questionnaire[firstLevelKey] = {};
                for (const option of firstLevelQuestionOptions) {
                    currentSection.questionnaire[firstLevelKey][option] = questionnaire[firstLevelKey][option];
                }
            }

            for (const secondLevelKey of secondLevelKeys) {
                if (questionnaire[firstLevelKey][secondLevelKey]._section) {
                    currentSection = sections.find(
                        (s) => s.name === questionnaire[firstLevelKey][secondLevelKey]._section,
                    );
                }
                if (currentSection) {
                    if (!currentSection.questionnaire[firstLevelKey]) {
                        currentSection.questionnaire[firstLevelKey] = {};
                        for (const option of firstLevelQuestionOptions) {
                            currentSection.questionnaire[firstLevelKey][option] = questionnaire[firstLevelKey][option];
                        }
                    }
                    currentSection.questionnaire[firstLevelKey][secondLevelKey] =
                        questionnaire[firstLevelKey][secondLevelKey];
                }
            }
        }
    },

    calculateStatuses: (questionnaire: Question, sections: Section[], answers: Answers) => {
        const result = [] as Section[];

        let hasInProgressSection = false;
        sections.forEach((section) => {
            let status: SectionStatus;
            if (!SectionsService.questionsService.checkCondition(section.questionForCondition, answers)) {
                //status = 'disabled';
                return;
            } else {
                const progress = SectionsService.questionsService.getQuestionnaireProgress(
                    section.questionnaire,
                    questionnaire,
                    answers,
                    {},
                );
                if (progress.isFinished) {
                    status = 'complete';
                } else if (!progress.isStarted) {
                    status = 'notStarted';
                } else {
                    if (hasInProgressSection) {
                        status = 'notStarted';
                    } else {
                        status = 'inProgress';
                        hasInProgressSection = true;
                    }
                }
            }

            result.push({ ...section, status });
        });

        if (!result.find((s) => s.status === 'inProgress')) {
            const firstNotStarted = result.find((s) => s.status === 'notStarted');
            if (firstNotStarted) {
                firstNotStarted.status = 'inProgress';
            }
        }

        return result;
    },

    getSubSectionText: (questionnaireType = '', subSection: string, productContext: string): string | null => {
        questionnaireType = questionnaireType.toLowerCase();
        if (!subSection) {
            return null;
        }
        if (productContext) {
            return t(
                `${questionnaireType}:subsections.${productContext}.${subSection}.title`,
                t(`${questionnaireType}:subsections.${subSection}.title`),
            );
        }
        return t(`${questionnaireType}:subsections.${subSection}.title`);
    },

    getSubSectionSecondLine: (questionnaireType: string, subSection: string, productContext: string) => {
        questionnaireType = questionnaireType.toLowerCase();
        if (!subSection) {
            return null;
        }
        let result = '';
        if (productContext) {
            result = t(`${questionnaireType}:subsections.${productContext}.${subSection}.second_line`, '');
        }
        if (!result) {
            result = t(
                `${questionnaireType}:subsections.${subSection}.second_line`,
                t(`${questionnaireType}:subsections.default_second_line`),
            );
        }
        return result;
    },

    getSubSectionArticleLabels: (questionnaireType = '', subSection: string, productContext: string): string[] => {
        questionnaireType = questionnaireType.toLowerCase();
        if (!subSection) {
            return [];
        }
        let value = [] as string[];
        if (productContext) {
            value =
                t(`${questionnaireType}:subsections.${productContext}.${subSection}.article_labels`, '', {
                    returnObjects: true,
                }) || [];
        }
        if (!value.length) {
            value =
                t(`${questionnaireType}:subsections.${subSection}.article_labels`, '', { returnObjects: true }) || [];
        }
        return value;
    },

    getSectionArticleLabels: (questionnaireType = '', section: string): string[] => {
        questionnaireType = questionnaireType.toLowerCase();
        if (!section) {
            return [];
        }
        return t(`${questionnaireType}:sections.${section}.article_labels`, '', { returnObjects: true }) || [];
    },
};

export default SectionsService;
